import { isNil } from 'lodash-es';
import dayjs from 'dayjs';
import type { AdditionForSqlDetail } from './types';
import { ONE_DAY, type TimeRangeInfo } from '@/common/utils';
import { UTC_TIME_FORMAT } from '@/common/utils/core';

interface DetailTitleMetadata {
  label: string;
  value?: string | number | null;
}

const getSqlDetailTitle = (data: DetailTitleMetadata[]) => {
  const formattedString = data.reduce<string[]>((acc, cur) => {
    if (isNil(cur.value)) {
      return acc;
    }
    const formattedLabel = cur.label ? `${cur.label}: ` : '';
    acc.push(`${formattedLabel}${cur.value}`);
    return acc;
  }, []);

  if (!formattedString.length) {
    return 'Error';
  }

  const [first, ...rest] = formattedString;
  if (!rest.length) {
    return first;
  }

  return `${first} (${rest.join(', ')})`;
};

export const getPostgresqlSqlDetailTitle = ({
  sqlId,
  queryId,
}: {
  sqlId?: string | number | null;
  queryId?: string | number | null;
}) =>
  getSqlDetailTitle([
    {
      label: 'SQL ID',
      value: sqlId,
    },
    {
      label: 'Query ID',
      value: queryId,
    },
  ]);

export const getAltibaseSqlDetailTitle = ({
  sqlId,
  stmtId,
}: {
  sqlId?: string | number | null;
  stmtId?: string | number | null;
}) =>
  getSqlDetailTitle([
    {
      label: 'SQL ID',
      value: sqlId,
    },
    {
      label: 'Stmt ID',
      value: stmtId,
    },
  ]);

export const getRedshiftSqlDetailTitle = ({
  sqlId,
  query,
}: {
  sqlId?: string | number | null;
  query?: string | number | null;
}) =>
  getSqlDetailTitle([
    {
      label: '',
      value: sqlId,
    },
    {
      label: 'Query',
      value: query,
    },
  ]);

export const checkIsRequiredFieldsSame = ({
  newDetailInfo,
  oldDetailInfo,
  fields,
}: {
  newDetailInfo: Omit<AdditionForSqlDetail, 'dbType' | 'instanceId'>;
  oldDetailInfo?: Omit<AdditionForSqlDetail, 'dbType' | 'instanceId'>;
  fields: string[];
}) => {
  if (
    oldDetailInfo?.fromTime !== newDetailInfo.fromTime ||
    oldDetailInfo?.toTime !== newDetailInfo.toTime
  ) {
    return false;
  }

  return fields.every(
    (field) => !!newDetailInfo[field] && newDetailInfo[field] === oldDetailInfo?.[field],
  );
};

export const getMinimumFromToTime = ({ fromTime, toTime }: TimeRangeInfo) => {
  const toTimeDayjs = dayjs(toTime);
  const fromTimeDayjs = dayjs(fromTime);

  const intervalValue = toTimeDayjs.diff(fromTimeDayjs);
  if (intervalValue <= ONE_DAY) {
    return {
      fromTime: toTimeDayjs.subtract(1, 'day').format(UTC_TIME_FORMAT),
      toTime: toTimeDayjs.format(UTC_TIME_FORMAT),
    };
  }
  if (intervalValue >= ONE_DAY * 30) {
    // 30d, API limit
    return {
      fromTime: toTimeDayjs.subtract(30, 'day').format(UTC_TIME_FORMAT),
      toTime: toTimeDayjs.format(UTC_TIME_FORMAT),
    };
  }
  return {
    fromTime: toTimeDayjs.subtract(intervalValue).format(UTC_TIME_FORMAT),
    toTime: toTimeDayjs.format(UTC_TIME_FORMAT),
  };
};
